import { useState, useEffect } from 'react';
import { createResource,getResources,deleteResource, updateResource} from '../../../router/api';
import toast from 'react-hot-toast';

interface OrderFormProps {
  order?: any;
  onBack: () => void;
}

export default function OrderForm({ order, onBack }: OrderFormProps) {
  const [formData, setFormData] = useState({
    client_id: '',
    date_commande: new Date().toISOString().split('T')[0],
    date_livraison_souhaitee: '',
    statut: 'BROUILLON',
    notes: '',
    conditions_paiement: '',
    lignes: [] as any[],
  });

  const [showProductModal, setShowProductModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<any>(null);
  const [productQuantity, setProductQuantity] = useState(1);
  const [productPrice, setProductPrice] = useState(0);
  const [productDiscount, setProductDiscount] = useState(0);
const [products, setProducts] = useState<any[]>([]);
  const [clients, setClients] = useState<any[]>([]);
  /* ================= PREFILL (MODIFICATION) ================= */
  useEffect(() => {
    if (!order) return;

    setFormData({
      client_id: order.client_id,
      date_commande: order.date_commande,
      date_livraison_souhaitee: order.date_livraison_souhaitee,
      statut: order.statut,
      notes: order.notes ?? '',
      conditions_paiement: order.conditions_paiement ?? '',
      lignes: order.lignes ?? [],
    });
  }, [order]);

  /* ================= AJOUT PRODUIT ================= */
  const handleAddProduct = () => {
    if (!selectedProduct) return;

    const totalHT =
      productPrice * productQuantity * (1 - productDiscount / 100);

    const newLine = {
      produit_id: selectedProduct.id,
      designation: selectedProduct.name,
      quantite_commandee: productQuantity,
      prix_unitaire_ht: productPrice,
      remise_ligne: productDiscount,
      total_ht: totalHT,
    };

    setFormData({
      ...formData,
      lignes: [...formData.lignes, newLine],
    });

    setShowProductModal(false);
    setSelectedProduct(null);
    setProductQuantity(1);
    setProductPrice(0);
    setProductDiscount(0);
  };

  const handleRemoveLine = (index: number) => {
    setFormData({
      ...formData,
      lignes: formData.lignes.filter((_, i) => i !== index),
    });
  };

  /* ================= TOTAUX ================= */
  const totalHT = formData.lignes.reduce(
    (sum, l) => sum + (l.total_ht || 0),
    0
  );
  const totalTVA = totalHT * 0.2;
  const totalTTC = totalHT + totalTVA;

  /* ================= SUBMIT ================= */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      if (order) {
        await updateResource(
          `bons-commande-clients`,order.id,
          formData
        );
        toast.success('Commande mise à jour');
      } else {
        await createResource(
          `bons-commande-clients`,
          formData
        );
        toast.success('Commande créée');
      }

      onBack();
    } catch (e: any) {
      toast.error(e.response?.data?.message || 'Erreur sauvegarde');
    }
  };

  /* ================= MOCK PRODUITS ================= */
  const mockProducts = products.length
    ? products
    : [
        { id: 1, name: 'Produit A', price: 100 },
        { id: 2, name: 'Produit B', price: 200 },
        { id: 3, name: 'Produit C', price: 300 },
      ];
  useEffect(() => {
    Promise.all([
      getResources(`produits`).then(r => setProducts(r.data ?? [])),
      getResources(`clients`).then(r => setClients(r.data.data ?? [])),
    ]).catch(() => toast.error('Erreur chargement données'));
  }, []);

  return (
    <form onSubmit={handleSubmit} className="space-y-6">

      {/* ================= INFOS COMMANDE ================= */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h3 className="font-semibold mb-4">Informations Commande</h3>

        <div className="grid grid-cols-3 gap-4">
          <input
            type="date"
            value={formData.date_commande}
            onChange={e =>
              setFormData({ ...formData, date_commande: e.target.value })
            }
            className="border rounded px-3 py-2"
          />

          <input
            type="date"
            value={formData.date_livraison_souhaitee}
            onChange={e =>
              setFormData({
                ...formData,
                date_livraison_souhaitee: e.target.value,
              })
            }
            className="border rounded px-3 py-2"
          />

          <select
            value={formData.statut}
            onChange={e =>
              setFormData({ ...formData, statut: e.target.value })
            }
            className="border rounded px-3 py-2"
          >
            <option value="BROUILLON">Brouillon</option>
            <option value="VALIDE">Validé</option>
            <option value="ANNULE">Annulé</option>
          </select>
        </div>

        <textarea
          value={formData.notes}
          onChange={e =>
            setFormData({ ...formData, notes: e.target.value })
          }
          className="w-full mt-4 border rounded px-3 py-2"
          placeholder="Notes internes"
        />
      </div>

      {/* ================= LIGNES ================= */}
      <div className="bg-white p-6 rounded-lg shadow">
        <div className="flex justify-between mb-4">
          <h3 className="font-semibold">Articles</h3>
          <button
            type="button"
            onClick={() => setShowProductModal(true)}
            className="bg-blue-600 text-white px-3 py-1 rounded"
          >
            + Ajouter
          </button>
        </div>

        {formData.lignes.map((l, i) => (
          <div
            key={i}
            className="flex justify-between items-center border-b py-2"
          >
            <span>{l.designation}</span>
            <span>{l.quantite_commandee}</span>
            <span>{l.total_ht.toLocaleString()} €</span>
            <button
              type="button"
              onClick={() => handleRemoveLine(i)}
              className="text-red-600"
            >
              ✕
            </button>
          </div>
        ))}

        <div className="text-right mt-4">
          <div>HT : {totalHT.toLocaleString()} €</div>
          <div>TVA : {totalTVA.toLocaleString()} €</div>
          <div className="font-bold">
            TTC : {totalTTC.toLocaleString()} €
          </div>
        </div>
      </div>

      {/* ================= ACTIONS ================= */}
      <div className="flex justify-end gap-3">
        <button
          type="button"
          onClick={onBack}
          className="border px-4 py-2 rounded"
        >
          Annuler
        </button>
        <button
          type="submit"
          className="bg-blue-600 text-white px-4 py-2 rounded"
        >
          {order ? 'Mettre à jour' : 'Créer la commande'}
        </button>
      </div>

      {/* ================= MODAL PRODUIT ================= */}
      {showProductModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center">
          <div className="bg-white p-6 rounded w-96 space-y-4">
            <select
              onChange={e => {
                const p = mockProducts.find(
                  m => m.id === Number(e.target.value)
                );
                setSelectedProduct(p);
                if (p) setProductPrice(p.price);
              }}
              className="w-full border px-3 py-2"
            >
              <option value="">Produit</option>
              {mockProducts.map(p => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>

            <input
              type="number"
              value={productQuantity}
              onChange={e => setProductQuantity(+e.target.value)}
              className="w-full border px-3 py-2"
            />

            <button
              type="button"
              onClick={handleAddProduct}
              className="w-full bg-blue-600 text-white py-2 rounded"
            >
              Ajouter
            </button>
          </div>
        </div>
      )}
    </form>
  );
}
